import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "../../../layout/DashboardLayout";
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Chip,
  CircularProgress,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Grid,
  Pagination,
  Alert,
} from "@mui/material";

import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HistoryIcon from "@mui/icons-material/History";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";

import Confetti from "react-confetti";
import { useAuth } from "../../../context/AuthContext";
import {
  getKycStatus,
  getAgencyAuditLog,
  getPccRequest,
  getProfileData,
} from "../../../api/auth";
import { useNavigate } from "react-router-dom";
import { brand } from "../../../theme";

const actionLogLabels = {
  "kyc.submitted": "KYC submitted",
  "pcc.processed": "PCC request processed",
  "contract.signed": "Contract signed",
};

const actionLogColors = {
  "kyc.submitted": "info",
  "pcc.processed": "warning",
  "contract.signed": "success",
};

export default function AgencyOwnerDashboard() {
  const { user, loading } = useAuth();
  const [kycStatus, setKycStatus] = useState("not_submitted");
  const [pccStatus, setPccStatus] = useState("Not Submitted");
  const [auditLogs, setAuditLogs] = useState([]);
  const [specialist, setSpecialist] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [triggerCelebration, setTriggerCelebration] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const progressCardRef = useRef(null);
  const [cardSize, setCardSize] = useState({ width: 0, height: 0 });
  const navigate = useNavigate();

  const getTier = () => (kycStatus === "Submitted" ? 1 : 0);

  const getNextTierRequirement = () => {
    if (tier === 0)
      return "Complete your KYC verification to unlock Tier 1 and access more features.";
    if (pccStatus === "Not Submitted")
      return "Request your Pseudo City Code (PCC) to continue your onboarding.";
    if (pccStatus === "Pending")
      return "Your PCC request is with the Helpdesk. You'll be notified by email once it's ready — typically within 5 business days.";
    if (pccStatus === "Rejected")
      return "Your PCC request was not approved. Please resubmit using the button below to continue.";
    if (pccStatus === "Approved")
      return "You're all set! Your PCC is active — explore our products and start booking.";
    return "Complete your KYC verification to unlock Tier 1 and access more features.";
  };

  useEffect(() => {
    if (!loading && user?.role === "agency_owner") {
      (async () => {
        try {
          const [kycRes, auditRes, pccRes, profileRes] = await Promise.all([
            getKycStatus(),
            user?.agencyId
              ? getAgencyAuditLog(user.agencyId)
              : Promise.resolve({ data: { logs: [] } }),
            getPccRequest(),
            getProfileData(),
          ]);
          const status = kycRes.data?.kycStatus || "not_submitted";
          if (status === "Submitted" && kycStatus !== "Submitted") {
            setTriggerCelebration(true);
            setTimeout(() => setTriggerCelebration(false), 10000);
          }
          setKycStatus(status);
          setAuditLogs(auditRes.data?.logs || []);
          setPccStatus(pccRes.data?.data?.status || "Not Submitted");
          setSpecialist(profileRes.data?.agency?.assignedSpecialist || null);
        } catch (err) {
          console.error(err);
          setError("Failed to load your dashboard data. Please refresh the page and try again.");
        } finally {
          setFetching(false);
        }
      })();
    } else {
      setFetching(false);
    }
  }, [user, loading, kycStatus]);

  useEffect(() => {
    const card = progressCardRef.current;
    if (!card) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setCardSize({ width, height });
    });
    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  if (loading || fetching) {
    return (
      <DashboardLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </DashboardLayout>
    );
  }

  const tier = getTier();
  const pccRequested = ["Pending", "Approved"].includes(pccStatus);
  const progressValue = pccRequested ? 75 : tier === 1 ? 50 : 20;

  const milestones = [
    { label: "Account Created", done: true },
    { label: "KYC Submitted", done: tier >= 1 },
    { label: "PCC Requested", done: pccRequested },
    { label: "Tier 3 Unlocked", done: false },
  ];

  return (
    <DashboardLayout>
      {/* Welcome header */}
      <Box mb={4}>
        <Typography variant="h5" sx={{ color: "#0f172a" }}>
          Welcome back, {user?.agencyName || user?.name} 👋
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
          Track your agency's progress and unlock new features.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Progress card */}
        <Grid item xs={12} md={7}>
          <Card
            ref={progressCardRef}
            sx={{ height: "100%", position: "relative", overflow: "hidden" }}
          >
            {triggerCelebration && (
              <Confetti
                width={cardSize.width}
                height={cardSize.height}
                recycle={false}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: 10,
                  pointerEvents: "none",
                }}
              />
            )}
            <CardContent sx={{ p: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" sx={{ color: "#0f172a" }}>
                  Agency Progress
                </Typography>
                {tier >= 1 ? (
                  <Chip
                    label="Tier 1 Unlocked"
                    color="success"
                    icon={<EmojiEventsIcon />}
                    size="small"
                  />
                ) : (
                  <Chip
                    label="Tier 0"
                    size="small"
                    sx={{ bgcolor: "#f1f5f9", color: "#64748b" }}
                  />
                )}
              </Box>

              {/* Progress bar */}
              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748b", fontWeight: 500 }}
                  >
                    Onboarding progress
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: brand.terracotta, fontWeight: 600 }}
                  >
                    {progressValue}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: tier >= 1 ? "#16a34a" : "#0284c7",
                    },
                  }}
                />
              </Box>

              {/* Milestones */}
              <Box>
                {milestones.map((m, i) => (
                  <Box
                    key={i}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    mb={1.25}
                  >
                    {m.done ? (
                      <CheckCircleIcon
                        sx={{ color: "#16a34a", fontSize: "1.1rem" }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={{ color: "#cbd5e1", fontSize: "1.1rem" }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: m.done ? "#0f172a" : "#94a3b8",
                        fontWeight: m.done ? 500 : 400,
                      }}
                    >
                      {m.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
                {getNextTierRequirement()}
              </Typography>

              {/* CTA buttons */}
              {tier === 0 && (
                <Button
                  variant="contained"
                  startIcon={<RocketLaunchIcon />}
                  onClick={() => navigate("/agency/kyc-form")}
                  fullWidth
                >
                  Complete KYC — Unlock Tier 1
                </Button>
              )}

              {tier === 1 && (
                <Box display="flex" gap={1.5} flexWrap="wrap">
                  <Button
                    variant="contained"
                    onClick={() => navigate("/pcc-request")}
                    sx={{ flex: 1, minWidth: 120 }}
                  >
                    Request PCC
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/products")}
                    sx={{ flex: 1, minWidth: 120 }}
                  >
                    Explore Products
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick links */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ color: "#0f172a", mb: 2 }}>
                Quick Access
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                {[
                  {
                    label: "View KYC Form",
                    path: "/agency/kyc-form",
                    icon: <AssignmentIcon fontSize="small" />,
                    disabled: false,
                  },
                  {
                    label: "Request PCC",
                    path: "/pcc-request",
                    icon: <RocketLaunchIcon fontSize="small" />,
                    disabled: kycStatus !== "Submitted",
                  },
                  {
                    label: "Explore Products",
                    path: "/products",
                    icon: <EmojiEventsIcon fontSize="small" />,
                    disabled: false,
                  },
                ].map((item) => (
                  <Box
                    key={item.path}
                    onClick={() => !item.disabled && navigate(item.path)}
                    title={item.disabled ? "Complete KYC first to access this" : undefined}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "10px",
                      border: "1px solid #e2e8f0",
                      cursor: item.disabled ? "not-allowed" : "pointer",
                      opacity: item.disabled ? 0.5 : 1,
                      transition: "all 0.15s ease",
                      ...(!item.disabled && {
                        "&:hover": {
                          bgcolor: "#f0f9ff",
                          borderColor: "#bae6fd",
                          "& .link-icon": { color: "#0284c7" },
                        },
                      }),
                    }}
                  >
                    <Box
                      className="link-icon"
                      sx={{ color: "#94a3b8", transition: "color 0.15s ease" }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="#0f172a"
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Your Sales/Account Specialist */}
      {specialist && (
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <SupportAgentIcon sx={{ color: brand.terracotta, fontSize: "1.1rem" }} />
              <Typography variant="h6" sx={{ color: "#0f172a" }}>
                Your Sales/Account Specialist
              </Typography>
            </Box>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="#94a3b8" display="block" mb={0.25}>
                  Name
                </Typography>
                <Typography variant="body2" fontWeight={500} color="#0f172a">
                  {specialist.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="#94a3b8" display="block" mb={0.25}>
                  Email
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <EmailIcon sx={{ fontSize: "0.95rem", color: "#94a3b8" }} />
                  <Typography
                    component="a"
                    href={`mailto:${specialist.email}`}
                    variant="body2"
                    fontWeight={500}
                    color={brand.terracotta}
                    sx={{ textDecoration: "none" }}
                  >
                    {specialist.email}
                  </Typography>
                </Box>
              </Grid>
              {specialist.phone && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="#94a3b8" display="block" mb={0.25}>
                    Phone
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <PhoneIcon sx={{ fontSize: "0.95rem", color: "#94a3b8" }} />
                    <Typography variant="body2" fontWeight={500} color="#0f172a">
                      {specialist.phone}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Account History */}
      {auditLogs.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <HistoryIcon sx={{ fontSize: "1.1rem", color: "#64748b" }} />
            <Typography variant="h6" sx={{ color: "#0f172a" }}>
              Account History
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <List dense disablePadding>
            {auditLogs.slice((historyPage - 1) * 5, historyPage * 5).map((log) => (
              <ListItem
                key={log._id}
                disablePadding
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: "8px",
                  bgcolor: "#f8fafc",
                  border: "1px solid #f1f5f9",
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Chip
                        label={actionLogLabels[log.action] || log.action}
                        size="small"
                        color={actionLogColors[log.action] || "default"}
                      />
                    </Box>
                  }
                  secondary={new Date(log.createdAt).toLocaleString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  secondaryTypographyProps={{
                    sx: { mt: 0.5, fontSize: "0.75rem" },
                  }}
                />
              </ListItem>
            ))}
          </List>
          {Math.ceil(auditLogs.length / 5) > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(auditLogs.length / 5)}
                page={historyPage}
                onChange={(_, v) => setHistoryPage(v)}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </Paper>
      )}
    </DashboardLayout>
  );
}
